import type { Reward, RewardUnlock, UnlockKind } from './config';
import { ACHIEVEMENT_COSMETICS } from './achievement-cosmetics';
import { SENITA_COSMETICS, unlockOf } from './cosmetics';
import { xpForLevel } from './progression';


export const ACHIEVEMENT_CATEGORIES = ['economy', 'collection', 'forge', 'boosters', 'market', 'social', 'dedication'] as const;
export type AchievementCategory = (typeof ACHIEVEMENT_CATEGORIES)[number];

export const ACHIEVEMENT_CATEGORY_LABELS: Record<AchievementCategory, string> = {
  economy: 'Économie',
  collection: 'Collection',
  forge: 'Forge',
  boosters: 'Boosters',
  market: 'Marché',
  social: 'Social',
  dedication: 'Assiduité',
};

export const ACHIEVEMENT_IDS = [
  'anima_balance',
  'anima_spent',
  'unique_characters',
  'owned_common',
  'owned_uncommon',
  'owned_rare',
  'owned_epic',
  'owned_legendary',
  'owned_mythic',
  'owned_divine',
  'owned_senita',
  'animes_completed',
  'fragments_earned',
  'fragments_spent',
  'upgrades',
  'boosters_opened',
  'lucky_boosters_opened',
  'trades_completed',
  'listing_sales',
  'listing_purchases',
  'auctions_sold',
  'auctions_won',
  'bids_placed',
  'friends_count',
  'referrals_count',
  'messages_sent',
  'playtime_hours',
  'login_streak',
  'login_days',
  'account_age_days',
  'quests_completed',
  'player_level',
] as const;

export type AchievementId = (typeof ACHIEVEMENT_IDS)[number];
export type AchievementMetrics = Record<AchievementId, number>;

export interface AchievementDef {
  id: AchievementId;
  title: string;
  
  description: string;
  category: AchievementCategory;
  
  base: number;
  
  growth: number;
  step?: number;
  
  rewardBase: number;
  
  rewardElasticity: number;
  
  secret?: true;
}

const def = (
  id: AchievementId,
  category: AchievementCategory,
  title: string,
  description: string,
  base: number,
  growth: number,
  rewardBase: number,
  rewardElasticity: number,
  step?: number,
): AchievementDef => ({ id, category, title, description, base, growth, rewardBase, rewardElasticity, ...(step ? { step } : {}) });


const GRIND = 1;

const STEADY = 0.9;

const FARMABLE = 0.75;

export const ACHIEVEMENTS: readonly AchievementDef[] = [
  def('anima_balance', 'economy', 'Anima', 'Avoir {n} Anima.', 10_000, 1.5, 300, 0.85),
  def('anima_spent', 'economy', 'Dépensier', 'Dépenser {n} Anima en boosters.', 2_500, 1.6, 200, GRIND),

  def('unique_characters', 'collection', 'Complétion', 'Posséder {n} personnages différents.', 10, 1.5, 150, GRIND),
  def('owned_common', 'collection', 'Communs', 'Posséder {n} personnages Communs.', 10, 1.5, 100, GRIND),
  def('owned_uncommon', 'collection', 'Peu communs', 'Posséder {n} personnages Peu communs.', 5, 1.5, 150, GRIND),
  def('owned_rare', 'collection', 'Rares', 'Posséder {n} personnages Rares.', 3, 1.5, 250, GRIND),
  def('owned_epic', 'collection', 'Épiques', 'Posséder {n} personnages Épiques.', 2, 1.5, 400, GRIND),
  def('owned_legendary', 'collection', 'Légendaires', 'Posséder {n} personnages Légendaires.', 1, 1.5, 700, GRIND),
  def('owned_mythic', 'collection', 'Mythiques', 'Posséder {n} personnages Mythiques.', 1, 1.45, 1_200, GRIND),
  def('owned_divine', 'collection', 'Divins', 'Posséder {n} personnages Divins.', 1, 1.4, 2_000, GRIND),
  { ...def('owned_senita', 'collection', 'Senita', 'Posséder {n} personnages Senita.', 1, 1.5, 1_000_000, GRIND), secret: true },
  def('animes_completed', 'collection', 'Séries complètes', 'Compléter {n} animes.', 1, 1.5, 300, GRIND),

  def('fragments_earned', 'forge', 'Fragments', 'Obtenir {n} fragments.', 100, 1.6, 150, GRIND),
  def('fragments_spent', 'forge', 'Fondeur', 'Dépenser {n} fragments.', 100, 1.6, 150, GRIND),
  def('upgrades', 'forge', 'Forgeron', 'Améliorer des cartes {n} fois.', 3, 1.5, 150, GRIND),

  def('boosters_opened', 'boosters', 'Ouvreur', 'Ouvrir {n} boosters.', 10, 1.5, 150, STEADY),
  def('lucky_boosters_opened', 'boosters', 'Chanceux', 'Ouvrir {n} Boosters Chanceux.', 1, 1.5, 300, 0.8),

  def('trades_completed', 'market', 'Échangiste', 'Conclure {n} échanges.', 1, 1.5, 100, FARMABLE),
  def('listing_sales', 'market', 'Vendeur', 'Vendre {n} cartes sur le marché.', 1, 1.5, 100, FARMABLE),
  def('listing_purchases', 'market', 'Acheteur', 'Acheter {n} cartes sur le marché.', 1, 1.5, 100, FARMABLE),
  def('auctions_sold', 'market', 'Commissaire-priseur', 'Vendre {n} cartes aux enchères.', 1, 1.5, 100, FARMABLE),
  def('auctions_won', 'market', 'Enchérisseur', 'Remporter {n} enchères.', 1, 1.5, 100, FARMABLE),
  def('bids_placed', 'market', 'Main levée', 'Placer {n} offres d’enchère.', 3, 1.5, 80, FARMABLE),

  def('friends_count', 'social', 'Amis', 'Avoir {n} amis.', 1, 1.4, 100, FARMABLE),
  def('referrals_count', 'social', 'Parrain', 'Parrainer {n} joueurs vérifiés.', 1, 1.35, 1_000, STEADY),
  def('messages_sent', 'social', 'Bavard', 'Envoyer {n} messages.', 10, 1.5, 80, FARMABLE),

  def('playtime_hours', 'dedication', 'Temps de jeu', 'Jouer {n} heures.', 1, 1.5, 150, STEADY),
  def('login_streak', 'dedication', 'Série', 'Se connecter {n} jours d’affilée.', 3, 1.4, 150, GRIND),
  def('login_days', 'dedication', 'Habitué', 'Se connecter {n} jours.', 3, 1.4, 120, GRIND),
  def('account_age_days', 'dedication', 'Ancienneté', 'Avoir un compte de {n} jours.', 7, 1.4, 150, GRIND),
  def('quests_completed', 'dedication', 'Quêtes', 'Terminer {n} quêtes.', 5, 1.5, 120, GRIND),
  def('player_level', 'dedication', 'Niveau', 'Atteindre le niveau {n}.', 5, 1, 300, GRIND, 5),
];

const BY_ID = new Map<string, AchievementDef>(ACHIEVEMENTS.map((achievement) => [achievement.id, achievement]));

export function achievementById(id: string): AchievementDef | null {
  return BY_ID.get(id) ?? null;
}


export const COSMETIC_TIER_STEP = 5;

export const MAX_CLAIMABLE_SHOWN = 99;


export function niceNumber(value: number): number {
  if (!Number.isFinite(value) || value >= Number.MAX_SAFE_INTEGER) return Number.MAX_SAFE_INTEGER;
  if (value < 100) return Math.max(1, Math.round(value));
  const unit = 10 ** (Math.floor(Math.log10(value)) - 1);
  return Math.round(value / unit) * unit;
}

const targetCache = new Map<AchievementId, number[]>();


export function achievementTarget(achievement: AchievementDef, tier: number): number {
  if (tier < 1) return 0;
  let targets = targetCache.get(achievement.id);
  if (!targets) {
    targets = [0];
    targetCache.set(achievement.id, targets);
  }
  for (let next = targets.length; next <= tier; next += 1) {
    const previous = targets[next - 1]!;
    if (previous >= Number.MAX_SAFE_INTEGER) {
      targets.push(Number.MAX_SAFE_INTEGER);
      continue;
    }
    const raw = achievement.step
      ? achievement.base + achievement.step * (next - 1)
      : niceNumber(achievement.base * achievement.growth ** (next - 1));
    targets.push(Math.max(raw, previous + 1));
  }
  return targets[tier]!;
}


const LEGACY_REWARD_GROWTH = 1.35;
const REWARD_GROWTH = LEGACY_REWARD_GROWTH;


function achievementEffort(achievement: AchievementDef, target: number): number {
  return achievement.id === 'player_level' ? xpForLevel(target) : target;
}


export function achievementMarginalEffort(achievement: AchievementDef, tier: number): number {
  if (tier < 1) return 0;
  return achievementEffort(achievement, achievementTarget(achievement, tier))
    - achievementEffort(achievement, achievementTarget(achievement, tier - 1));
}

const animaCache = new Map<AchievementId, number[]>();


export function achievementAnima(achievement: AchievementDef, tier: number): number {
  if (tier < 1) return 0;
  let values = animaCache.get(achievement.id);
  if (!values) {
    values = [0];
    animaCache.set(achievement.id, values);
  }
  const firstEffort = Math.max(1, achievementMarginalEffort(achievement, 1));
  for (let next = values.length; next <= tier; next += 1) {
    const ratio = Math.max(1, achievementMarginalEffort(achievement, next) / firstEffort);
    const scaled = achievement.rewardBase * ratio ** achievement.rewardElasticity;
    // Linear targets (levels) would make the old exponential curve explode; they follow EXP only.
    const legacy = achievement.step ? 0 : achievement.rewardBase * LEGACY_REWARD_GROWTH ** (next - 1);
    values.push(Math.max(values[next - 1]!, niceNumber(Math.max(scaled, legacy))));
  }
  return values[tier]!;
}

export function achievementCosmetic(achievement: AchievementDef, tier: number): RewardUnlock | null {
  if (tier < COSMETIC_TIER_STEP || tier % COSMETIC_TIER_STEP !== 0) return null;
  const entry = ACHIEVEMENT_COSMETICS[achievement.id]?.[tier / COSMETIC_TIER_STEP - 1];
  return entry ? unlockOf(entry[0], entry[1]) : null;
}


function secretReward(achievement: AchievementDef, tier: number): Reward {
  const reward: Reward = {
    anima: niceNumber(achievement.rewardBase * REWARD_GROWTH ** (tier - 1)),
    xp: niceNumber(500 * 1.25 ** (tier - 1)),
    boosters: { legendary: 2 + tier },
  };
  if (tier === 1) {
    reward.unlocks = [
      unlockOf('title', SENITA_COSMETICS.title),
      unlockOf('frame', SENITA_COSMETICS.frame),
      unlockOf('banner', SENITA_COSMETICS.banner),
    ];
  }
  return reward;
}


export function isAchievementVisible(achievement: AchievementDef, tier: number, progress: number): boolean {
  return !achievement.secret || tier > 0 || progress >= achievementTarget(achievement, 1);
}

export function achievementReward(achievement: AchievementDef, tier: number): Reward {
  if (achievement.secret) return secretReward(achievement, tier);
  const reward: Reward = {
    anima: achievementAnima(achievement, tier),
    xp: niceNumber(20 * 1.25 ** (tier - 1)),
  };
  const extra = 1 + Math.floor(tier / 10);
  if (tier % COSMETIC_TIER_STEP === 0) reward.boosters = { legendary: extra };
  else if (tier % 2 === 0) reward.boosters = { premium: extra };
  else if (tier >= 3) reward.boosters = { standard: extra };
  const cosmetic = achievementCosmetic(achievement, tier);
  if (cosmetic) reward.unlocks = [cosmetic];
  return reward;
}


export function claimableTiers(achievement: AchievementDef, claimed: number, value: number): number {
  let count = 0;
  while (count < MAX_CLAIMABLE_SHOWN && value >= achievementTarget(achievement, claimed + count + 1)) count += 1;
  return count;
}

export function achievementDescription(achievement: AchievementDef, target: number): string {
  return achievement.description.replaceAll('{n}', target.toLocaleString('fr-FR'));
}

let sources: Map<string, { achievement: AchievementDef; tier: number }> | null = null;


export function cosmeticSource(kind: UnlockKind, key: string): { achievement: AchievementDef; tier: number } | null {
  if (!sources) {
    sources = new Map();
    for (const achievement of ACHIEVEMENTS) {
      if (achievement.secret) {
        for (const unlock of secretReward(achievement, 1).unlocks ?? []) {
          sources.set(`${unlock.kind}:${unlock.key}`, { achievement, tier: 1 });
        }
        continue;
      }
      (ACHIEVEMENT_COSMETICS[achievement.id] ?? []).forEach(([entryKind, entryKey], index) => {
        sources!.set(`${entryKind}:${entryKey}`, { achievement, tier: (index + 1) * COSMETIC_TIER_STEP });
      });
    }
  }
  return sources.get(`${kind}:${key}`) ?? null;
}
